import type { ReactNode } from 'react';
import { Plus, Search } from 'lucide-react';
import { useIsDesktop } from '@/lib/hooks';
import { useUI } from '@/state/ui';
import { Button, IconButton, Kbd } from '@/components/ui/button';
import { SyncIndicator } from './SyncIndicator';

/** Top bar of a page: breadcrumb/title on the left, search and "+ Task" on the right. */
export function PageHeader({
  title,
  crumb,
  actions,
  onAddTask,
}: {
  title: string;
  crumb?: ReactNode;
  actions?: ReactNode;
  onAddTask?: () => void;
}) {
  const desktop = useIsDesktop();
  const setPalette = useUI((s) => s.setPaletteOpen);
  const openQuickAdd = useUI((s) => s.openQuickAdd);
  if (!desktop) {
    return (
      <header
        className="safe-top flex h-[52px] shrink-0 items-center gap-2 px-4"
        style={{ minHeight: 'calc(52px + env(safe-area-inset-top, 0px))' }}
      >
        <div className="min-w-0 flex-1">
          {crumb && <div className="truncate text-cap text-ink-3">{crumb}</div>}
          <h1 className="truncate text-lg font-semibold">{title}</h1>
        </div>
        <SyncIndicator />
        {actions}
        <IconButton label="Search" size="lg" onClick={() => setPalette(true)}>
          <Search className="size-5" />
        </IconButton>
      </header>
    );
  }
  return (
    <header className="flex h-11 shrink-0 items-center gap-2 border-b border-line px-4">
      <div className="flex min-w-0 items-center gap-1.5 text-sm text-ink-2">
        {crumb ?? <h1 className="font-semibold text-ink">{title}</h1>}
      </div>
      <span className="flex-1" />
      <SyncIndicator />
      {actions}
      <button
        onClick={() => setPalette(true)}
        className="flex h-7 w-60 items-center gap-2 rounded-sm bg-surface-2 px-2.5 text-sm text-ink-3 hover:bg-surface-3 max-lg:w-40"
        aria-label="Search (Ctrl K)"
      >
        <Search className="size-3.5" />
        <span className="flex-1 truncate text-left">Search or add a task</span>
        <Kbd>Ctrl K</Kbd>
      </button>
      <Button
        variant="primary"
        icon={<Plus className="size-3.5" />}
        onClick={() => (onAddTask ? onAddTask() : openQuickAdd({ tab: 'task' }))}
      >
        Task
      </Button>
    </header>
  );
}
